export interface ThemeColors {
  primary: {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
    950: string;
  };
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
}

export const blueTheme: ThemeColors = {
  // Brand: use #C1156D across UI surfaces
  primary: {
    50: '#C1156D',
    100: '#C1156D',
    200: '#C1156D',
    300: '#C1156D',
    400: '#C1156D',
    500: '#C1156D',
    600: '#C1156D',
    700: '#C1156D',
    800: '#C1156D',
    900: '#C1156D',
    950: '#C1156D',
  },
  primaryForeground: '#ffffff',
  secondary: '#fff7ed',
  secondaryForeground: '#0f172a',
  background: '#ffffff',
  foreground: '#0f172a',
  card: '#ffffff',
  cardForeground: '#0f172a',
  popover: '#ffffff',
  popoverForeground: '#0f172a',
  muted: '#f8fafc',
  mutedForeground: '#64748b',
  accent: '#C1156D',
  accentForeground: '#0f172a',
  destructive: '#ef4444',
  destructiveForeground: '#ffffff',
  border: '#e2e8f0',
  input: '#e2e8f0',
  ring: '#C1156D',
};

export const blueDarkTheme: ThemeColors = {
  // Brand (dark): use #C1156D with dark surfaces
  primary: {
    50: '#C1156D',
    100: '#C1156D',
    200: '#C1156D',
    300: '#C1156D',
    400: '#C1156D',
    500: '#C1156D',
    600: '#C1156D',
    700: '#C1156D',
    800: '#C1156D',
    900: '#C1156D',
    950: '#C1156D',
  },
  primaryForeground: '#ffffff',
  secondary: '#3b2a12',
  secondaryForeground: '#f8fafc',
  background: '#0f172a',
  foreground: '#f8fafc',
  card: '#111827',
  cardForeground: '#f8fafc',
  popover: '#111827',
  popoverForeground: '#f8fafc',
  muted: '#1f2937',
  mutedForeground: '#94a3b8',
  accent: '#C1156D',
  accentForeground: '#0f172a',
  destructive: '#7f1d1d',
  destructiveForeground: '#f8fafc',
  border: '#374151',
  input: '#374151',
  ring: '#C1156D',
};

export const pinkTheme: ThemeColors = {
  primary: {
    50: '#fdf2f8',
    100: '#fce7f3',
    200: '#fbcfe8',
    300: '#f9a8d4',
    400: '#f472b6',
    500: '#ec4899',
    600: '#db2777',
    700: '#be185d',
    800: '#9d174d',
    900: '#831843',
    950: '#500724',
  },
  primaryForeground: '#ffffff',
  secondary: '#f1f5f9',
  secondaryForeground: '#0f172a',
  background: '#ffffff',
  foreground: '#0f172a',
  card: '#ffffff',
  cardForeground: '#0f172a',
  popover: '#ffffff',
  popoverForeground: '#0f172a',
  muted: '#f8fafc',
  mutedForeground: '#64748b',
  accent: '#f1f5f9',
  accentForeground: '#0f172a',
  destructive: '#ef4444',
  destructiveForeground: '#ffffff',
  border: '#e2e8f0',
  input: '#e2e8f0',
  ring: '#ec4899',
};

export const pinkDarkTheme: ThemeColors = {
  primary: {
    50: '#4a043e',
    100: '#500724',
    200: '#831843',
    300: '#9d174d',
    400: '#be185d',
    500: '#db2777',
    600: '#ec4899',
    700: '#f472b6',
    800: '#f9a8d4',
    900: '#fbcfe8',
    950: '#fdf2f8',
  },
  primaryForeground: '#ffffff',
  secondary: '#1e293b',
  secondaryForeground: '#f8fafc',
  background: '#0f172a',
  foreground: '#f8fafc',
  card: '#1e293b',
  cardForeground: '#f8fafc',
  popover: '#1e293b',
  popoverForeground: '#f8fafc',
  muted: '#1e293b',
  mutedForeground: '#94a3b8',
  accent: '#334155',
  accentForeground: '#f8fafc',
  destructive: '#7f1d1d',
  destructiveForeground: '#f8fafc',
  border: '#334155',
  input: '#334155',
  ring: '#ec4899',
};

export type ThemeColor = 'blue' | 'pink';

export const themes = {
  blue: { light: blueTheme, dark: blueDarkTheme },
  pink: { light: pinkTheme, dark: pinkDarkTheme },
};

export const defaultThemeColor: ThemeColor = 'blue';

export function applyTheme(themeColor: ThemeColor, mode: 'light' | 'dark') {
  const theme = themes[themeColor][mode];
  const root = document.documentElement;

  // Apply primary color scale
  Object.entries(theme.primary).forEach(([shade, color]) => {
    root.style.setProperty(`--primary-${shade}`, color);
  });

  // Apply semantic colors
  root.style.setProperty('--primary', theme.primary[500]);
  root.style.setProperty('--primary-foreground', theme.primaryForeground);
  root.style.setProperty('--secondary', theme.secondary);
  root.style.setProperty('--secondary-foreground', theme.secondaryForeground);
  root.style.setProperty('--background', theme.background);
  root.style.setProperty('--foreground', theme.foreground);
  root.style.setProperty('--card', theme.card);
  root.style.setProperty('--card-foreground', theme.cardForeground);
  root.style.setProperty('--popover', theme.popover);
  root.style.setProperty('--popover-foreground', theme.popoverForeground);
  root.style.setProperty('--muted', theme.muted);
  root.style.setProperty('--muted-foreground', theme.mutedForeground);
  root.style.setProperty('--accent', theme.accent);
  root.style.setProperty('--accent-foreground', theme.accentForeground);
  root.style.setProperty('--destructive', theme.destructive);
  root.style.setProperty('--destructive-foreground', theme.destructiveForeground);
  root.style.setProperty('--border', theme.border);
  root.style.setProperty('--input', theme.input);
  root.style.setProperty('--ring', theme.ring);

  // Update meta theme color
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', theme.background);
  }
}